import {
    Autocomplete,
    Avatar,
    Box,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    InputBase,
    TextField,
    Typography
} from "@mui/material";
import {
    Search as SearchIcon,
    CheckCircle as CheckCircleIcon,
    FitnessCenter as FitnessCenterIcon,
    Close as CloseIcon,
} from "@mui/icons-material";
import { useState, useCallback, useEffect } from "react";
import InfiniteScroll from "react-infinite-scroller";
import { getExercises } from "../../services/ExcersiceService";
import { RTL } from "../../cache/CacheRtl";
import { SUPERSET_COLORS, getMuscleGroupColor } from "../../utils/exerciseUtils";
import { targetMuscles } from "../../enums/TargetMuscles";
import MyDialog from "../shared/Dialog";
import { imageUrl as resizedImageUrl } from "../../utils/imageUrl";

const targetMuscleMap = new Map(targetMuscles.map(item => [item.value, item.name]));

const AddSuperSetDialog = ({ isOpen, close, onAdd }) => {
    const [activeSlot, setActiveSlot] = useState('A'); // 'A' or 'B'
    const [exerciseA, setExerciseA] = useState(null);
    const [exerciseB, setExerciseB] = useState(null);
    const [keyword, setKeyword] = useState("");
    const [targetMuscle, setTargetMuscle] = useState(null);
    const [exercises, setExercises] = useState([]);
    const [pageId, setPageId] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setActiveSlot('A');
            setExerciseA(null);
            setExerciseB(null);
            setKeyword("");
            setTargetMuscle(null);
            setExercises([]);
            setPageId(0);
            setHasMore(true);
            setIsLoading(false);
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        setExercises([]);
        setPageId(0);
        setHasMore(true);
    }, [keyword, targetMuscle, isOpen]);

    const fetchNextPage = useCallback(() => {
        if (isLoading) return;
        setIsLoading(true);

        getExercises(pageId, 20, keyword || undefined, undefined, targetMuscle, undefined, undefined, true)
            .then((res) => {
                const content = res.data.content;
                setExercises((prev) => pageId === 0 ? content : [...prev, ...content]);
                setPageId((prev) => prev + 1);
                setHasMore(content.length > 0);
            })
            .catch((e) => {
                console.error("Error fetching exercises:", e);
                setHasMore(false);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [pageId, keyword, targetMuscle, isLoading]);

    const getTargetMuscleLabel = (value) => {
        return targetMuscleMap.get(value) || value || '';
    };

    const handleSelectExercise = (exercise) => {
        if (activeSlot === 'A') {
            // If selecting A and this is already B, clear B
            if (exerciseB && exercise.id === exerciseB.id) {
                setExerciseB(null);
            }
            setExerciseA(exercise);
            // Auto-advance to B if B not yet selected
            if (!exerciseB) {
                setActiveSlot('B');
            }
        } else {
            // Don't allow same exercise for both
            if (exercise.id === exerciseA?.id) return;
            setExerciseB(exercise);
        }
    };

    const handleClearA = (e) => {
        e.stopPropagation();
        setExerciseA(null);
        setExerciseB(null); // Clear B too since A is the first step
        setActiveSlot('A');
    };

    const handleClearB = (e) => {
        e.stopPropagation();
        setExerciseB(null);
        setActiveSlot('B');
    };

    const handleSlotClick = (slot) => {
        setActiveSlot(slot);
    };

    const handleAdd = () => {
        if (exerciseA && exerciseB && onAdd) {
            onAdd(exerciseA, exerciseB);
            close();
        }
    };

    const bothSelected = exerciseA && exerciseB;

    const renderLoader = () => (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
        </Box>
    );

    return (
        <MyDialog
            isOpen={isOpen}
            close={close}
            title="הוסף סופר סט"
            showCloseButton
            actions={
                <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                    <RTL>
                        <Button
                            variant="contained"
                            onClick={handleAdd}
                            disabled={!bothSelected}
                            sx={{
                                backgroundColor: SUPERSET_COLORS.accent,
                                borderRadius: "8px",
                                textTransform: "none",
                                fontWeight: 600,
                                px: 4,
                                "&:hover": { backgroundColor: SUPERSET_COLORS.accentLight },
                                "&:disabled": { backgroundColor: "#E2E8F0", color: "#94A3B8" }
                            }}
                        >
                            הוסף סופר סט
                        </Button>
                    </RTL>
                </Box>
            }
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '60vh', direction: 'rtl' }}>

                {/* Step Indicator - Two Slots */}
                <Box sx={{
                    display: 'flex',
                    gap: 1.5,
                    mx: 1,
                    mt: 2,
                    mb: 1.5,
                }}>
                    {/* Slot A */}
                    <Box
                        onClick={() => handleSlotClick('A')}
                        sx={{
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            p: 1.5,
                            borderRadius: '10px',
                            border: '2px solid',
                            borderColor: activeSlot === 'A' ? SUPERSET_COLORS.badgeA : exerciseA ? SUPERSET_COLORS.border : '#E2E8F0',
                            backgroundColor: exerciseA ? SUPERSET_COLORS.background : activeSlot === 'A' ? '#FAFAFE' : '#FAFBFC',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            position: 'relative',
                            '&:hover': {
                                borderColor: SUPERSET_COLORS.badgeA,
                            }
                        }}
                    >
                        <Box sx={{
                            width: 28, height: 28, borderRadius: '50%',
                            backgroundColor: exerciseA ? SUPERSET_COLORS.badgeA : activeSlot === 'A' ? SUPERSET_COLORS.badgeA : '#CBD5E1',
                            color: 'white',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '0.7rem', fontWeight: 700, flexShrink: 0,
                            transition: 'background-color 0.2s',
                        }}>
                            {exerciseA ? <CheckCircleIcon sx={{ fontSize: 16 }} /> : "א'"}
                        </Box>

                        {exerciseA ? (
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{
                                    fontSize: '0.75rem',
                                    color: '#94A3B8',
                                    lineHeight: 1,
                                    mb: 0.25,
                                }}>
                                    תרגיל א'
                                </Typography>
                                <Typography sx={{
                                    fontSize: '0.8rem',
                                    fontWeight: 600,
                                    color: SUPERSET_COLORS.text,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}>
                                    {exerciseA.nameHeb || exerciseA.name}
                                </Typography>
                            </Box>
                        ) : (
                            <Typography sx={{
                                flex: 1,
                                fontSize: '0.8rem',
                                fontWeight: activeSlot === 'A' ? 600 : 400,
                                color: activeSlot === 'A' ? SUPERSET_COLORS.text : '#94A3B8',
                            }}>
                                בחר תרגיל א'
                            </Typography>
                        )}

                        {exerciseA && (
                            <IconButton
                                onClick={handleClearA}
                                size="small"
                                sx={{ p: 0.25, color: '#94A3B8', '&:hover': { color: 'error.main' } }}
                            >
                                <CloseIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        )}
                    </Box>

                    {/* Connector line */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        color: bothSelected ? SUPERSET_COLORS.accent : '#CBD5E1',
                        fontSize: '1.2rem',
                        fontWeight: 700,
                        transition: 'color 0.2s',
                    }}>
                        +
                    </Box>

                    {/* Slot B */}
                    <Box
                        onClick={() => exerciseA && handleSlotClick('B')}
                        sx={{
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            p: 1.5,
                            borderRadius: '10px',
                            border: '2px solid',
                            borderColor: activeSlot === 'B' ? SUPERSET_COLORS.badgeB : exerciseB ? SUPERSET_COLORS.border : '#E2E8F0',
                            backgroundColor: exerciseB ? SUPERSET_COLORS.background : activeSlot === 'B' ? '#FAFAFE' : '#FAFBFC',
                            cursor: exerciseA ? 'pointer' : 'default',
                            opacity: exerciseA ? 1 : 0.5,
                            transition: 'all 0.2s ease',
                            position: 'relative',
                            '&:hover': {
                                borderColor: exerciseA ? SUPERSET_COLORS.badgeB : '#E2E8F0',
                            }
                        }}
                    >
                        <Box sx={{
                            width: 28, height: 28, borderRadius: '50%',
                            backgroundColor: exerciseB ? SUPERSET_COLORS.badgeB : activeSlot === 'B' ? SUPERSET_COLORS.badgeB : '#CBD5E1',
                            color: 'white',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '0.7rem', fontWeight: 700, flexShrink: 0,
                            transition: 'background-color 0.2s',
                        }}>
                            {exerciseB ? <CheckCircleIcon sx={{ fontSize: 16 }} /> : "ב'"}
                        </Box>

                        {exerciseB ? (
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{
                                    fontSize: '0.75rem',
                                    color: '#94A3B8',
                                    lineHeight: 1,
                                    mb: 0.25,
                                }}>
                                    תרגיל ב'
                                </Typography>
                                <Typography sx={{
                                    fontSize: '0.8rem',
                                    fontWeight: 600,
                                    color: SUPERSET_COLORS.text,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}>
                                    {exerciseB.nameHeb || exerciseB.name}
                                </Typography>
                            </Box>
                        ) : (
                            <Typography sx={{
                                flex: 1,
                                fontSize: '0.8rem',
                                fontWeight: activeSlot === 'B' ? 600 : 400,
                                color: activeSlot === 'B' ? SUPERSET_COLORS.text : '#94A3B8',
                            }}>
                                בחר תרגיל ב'
                            </Typography>
                        )}

                        {exerciseB && (
                            <IconButton
                                onClick={handleClearB}
                                size="small"
                                sx={{ p: 0.25, color: '#94A3B8', '&:hover': { color: 'error.main' } }}
                            >
                                <CloseIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        )}
                    </Box>
                </Box>

                {/* Active slot indicator text */}
                <Typography sx={{
                    px: 2,
                    pb: 1,
                    fontSize: '0.75rem',
                    color: SUPERSET_COLORS.accent,
                    fontWeight: 600,
                    textAlign: 'center',
                }}>
                    {bothSelected
                        ? 'לחץ על "הוסף סופר סט" או שנה את הבחירה'
                        : activeSlot === 'A'
                            ? 'בחר את התרגיל הראשון'
                            : 'בחר את התרגיל השני'
                    }
                </Typography>

                {/* Search & Filter Row */}
                <Box sx={{
                    display: 'flex',
                    gap: 1,
                    alignItems: 'center',
                    mx: 1,
                    mb: 1,
                    flexWrap: 'wrap',
                }}>
                    <Box sx={{
                        backgroundColor: "#FFFFFF",
                        border: "1px solid #E2E8F0",
                        borderRadius: "8px",
                        display: "flex",
                        alignItems: "center",
                        flex: 1,
                        minWidth: 180,
                    }}>
                        <IconButton sx={{ padding: "8px", color: "#64748B" }}>
                            <SearchIcon fontSize="small" />
                        </IconButton>
                        <InputBase
                            value={keyword}
                            onChange={(e) => setKeyword(e.target.value)}
                            sx={{
                                flex: 1,
                                fontSize: "14px",
                                "& input::placeholder": { color: "#94A3B8" }
                            }}
                            placeholder="חיפוש לפי שם תרגיל..."
                            autoFocus
                        />
                    </Box>

                    <RTL>
                        <Autocomplete
                            disablePortal
                            value={targetMuscle}
                            getOptionLabel={(option) => option?.name || ""}
                            onChange={(_event, value) => setTargetMuscle(value || null)}
                            options={targetMuscles}
                            sx={{
                                width: 180,
                                "& .MuiOutlinedInput-root": {
                                    backgroundColor: "#FFFFFF",
                                    borderRadius: "8px",
                                    "& fieldset": { borderColor: "#E2E8F0" }
                                },
                                "& .MuiInputLabel-root": {
                                    color: "#64748B",
                                    fontSize: "14px"
                                }
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="קבוצת שרירים"
                                    size="small"
                                />
                            )}
                        />
                    </RTL>
                </Box>

                {/* Exercise List */}
                <Box sx={{ flex: 1, overflow: 'auto', px: 1 }} data-superset-scroll>
                    <InfiniteScroll
                        initialLoad={true}
                        hasMore={hasMore}
                        loadMore={fetchNextPage}
                        loader={renderLoader()}
                        useWindow={false}
                        getScrollParent={() => document.querySelector('[data-superset-scroll]')}
                    >
                        {exercises.map((exercise) => {
                            const isSelectedAsA = exerciseA && exercise.id === exerciseA.id;
                            const isSelectedAsB = exerciseB && exercise.id === exerciseB.id;
                            const isSelected = isSelectedAsA || isSelectedAsB;
                            // Disable if: selecting for B and this is already A, or selecting for A and this is already B
                            const isDisabled = (activeSlot === 'B' && isSelectedAsA) || (activeSlot === 'A' && isSelectedAsB);
                            const imageUrl = exercise.imageUrl || exercise.coverUrl;
                            const muscleLabel = getTargetMuscleLabel(exercise.targetMuscle);
                            const muscleColor = getMuscleGroupColor(exercise.targetMuscle);

                            return (
                                <Box
                                    key={exercise.id}
                                    onClick={() => !isDisabled && handleSelectExercise(exercise)}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.5,
                                        py: 1,
                                        px: 1.5,
                                        borderBottom: '1px solid #F1F5F9',
                                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                                        borderRadius: '8px',
                                        opacity: isDisabled ? 0.35 : 1,
                                        backgroundColor: isSelected && !isDisabled
                                            ? SUPERSET_COLORS.background
                                            : 'transparent',
                                        '&:hover': {
                                            backgroundColor: isDisabled ? 'transparent' :
                                                isSelected ? SUPERSET_COLORS.backgroundHover : '#F8FAFC'
                                        },
                                        transition: 'background-color 0.15s ease'
                                    }}
                                >
                                    {/* Thumbnail */}
                                    <Avatar
                                        src={resizedImageUrl(imageUrl, 44)}
                                        variant="rounded"
                                        sx={{
                                            width: 44,
                                            height: 44,
                                            backgroundColor: '#F1F5F9',
                                            borderRadius: '8px',
                                        }}
                                    >
                                        <FitnessCenterIcon sx={{ color: '#CBD5E1', fontSize: 20 }} />
                                    </Avatar>

                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography sx={{
                                            fontSize: '0.85rem',
                                            fontWeight: 500,
                                            color: '#1E293B',
                                            lineHeight: 1.3,
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        }}>
                                            {exercise.nameHeb || exercise.name}
                                        </Typography>
                                        {muscleLabel && (
                                            <Chip
                                                label={muscleLabel}
                                                size="small"
                                                sx={{
                                                    mt: 0.5,
                                                    height: 20,
                                                    fontSize: '0.65rem',
                                                    fontWeight: 600,
                                                    backgroundColor: `${muscleColor}15`,
                                                    color: muscleColor,
                                                    border: `1px solid ${muscleColor}30`,
                                                    '& .MuiChip-label': { px: 1 }
                                                }}
                                            />
                                        )}
                                    </Box>

                                    {/* Selection badge */}
                                    {isSelectedAsA && (
                                        <Chip
                                            label="א'"
                                            size="small"
                                            sx={{
                                                height: 22,
                                                fontSize: '0.7rem',
                                                fontWeight: 700,
                                                backgroundColor: SUPERSET_COLORS.badgeA,
                                                color: 'white',
                                                '& .MuiChip-label': { px: 0.75 },
                                            }}
                                        />
                                    )}
                                    {isSelectedAsB && (
                                        <Chip
                                            label="ב'"
                                            size="small"
                                            sx={{
                                                height: 22,
                                                fontSize: '0.7rem',
                                                fontWeight: 700,
                                                backgroundColor: SUPERSET_COLORS.badgeB,
                                                color: 'white',
                                                '& .MuiChip-label': { px: 0.75 },
                                            }}
                                        />
                                    )}
                                </Box>
                            );
                        })}

                        {exercises.length === 0 && !hasMore && !isLoading && (
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'text.secondary',
                                    textAlign: 'center',
                                    py: 4,
                                    fontStyle: 'italic'
                                }}
                            >
                                לא נמצאו תרגילים
                            </Typography>
                        )}
                    </InfiniteScroll>
                </Box>
            </Box>
        </MyDialog>
    );
};

export default AddSuperSetDialog;
